// ================================================================
//  ApiCollectionEditDialogController
// ================================================================

angular.module('bizmetryApp').controller('ApiCollectionEditDialogController', function (
    $scope,
    $mdDialog,
    $timeout,
    $q,
    ApiCollectionService,
    TelemetryAgentService,
    DatasourceService,
    dialogService,
    sessionInfo,
    collection
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    // ── Injected data ──
    ctrl.collection = collection;
    ctrl.sourceType  = collection.swagger_source
        ? collection.swagger_source.source_type
        : null;

    // ── Editable model ──
    ctrl.model = {
        description:  collection.description  || '',
        selectedAgent: null,   // pre-populated after agents load (URL mode)
        swaggerPath:  _pathFromUrl(collection.swagger_source)
    };

    // ── Original snapshot for dirty detection ──
    const _orig = {
        description: collection.description || '',
        swaggerPath: _pathFromUrl(collection.swagger_source),
        agentId:     _agentIdFromSource(collection.swagger_source),
        hasNewSwagger: false
    };

    // ── Agents (URL mode) ──
    ctrl.onlineAgents   = [];
    ctrl.loadingAgents  = false;

    // ── URL mode state ──
    ctrl.urlError    = null;
    ctrl.isFetching  = false;
    ctrl.fetchedOk   = false;
    ctrl.fetchError  = null;
    ctrl.fetchedLabel = '';
    ctrl._urlDebounce = null;

    // ── File mode state ──
    ctrl.newFile          = null;
    ctrl.newFileSizeLabel = '';
    ctrl.newFileParsedOk  = false;
    ctrl.newFileError     = null;
    ctrl.newFileLabel     = '';

    // ── New spec (either from file or fetch) ──
    ctrl.newSpecObj      = null;
    ctrl.newSpecRaw      = null;
    ctrl.newSpecVersion  = null;
    ctrl.newSpecTitle    = null;
    ctrl.versionMismatch = false;
    ctrl.titleMismatch   = false;

    // ── Save state ──
    ctrl.isSaving = false;

    // ── Contract summary + Swagger editor ──
    // Contract confirmed in the Swagger editor, staged until "Apply Changes":
    // { spec, text, operationsCount, changes, changesTotal } or null.
    ctrl.editorStaged = null;
    _refreshSummary();

    // A PUBLISHED collection is immutable: "Apply" forks a new DRAFT version
    // (POST /fork — major bump on breaking changes, minor otherwise).
    ctrl.isPublished = collection.status === 'PUBLISHED';

    var URL_PATTERN = /^(?:[\w.-]+|\d{1,3}(?:\.\d{1,3}){3})(?::\d+)?(?:\/[^\s]*)?$/;

    // ================================================================
    // INIT
    // ================================================================

    ctrl.$onInit = function () {
        if (ctrl.sourceType === 'HTTP_URL') {
            _loadAgents();
        }
    };

    // ================================================================
    // AGENTS
    // ================================================================

    function _loadAgents() {
        ctrl.loadingAgents = true;
        DatasourceService.getReachableAgentsByAccount(sessionInfo.accountId)
            .then(function (agents) {
                ctrl.onlineAgents = agents || [];
                // Pre-select the agent that was used originally
                var origAgentId = _agentIdFromSource(collection.swagger_source);
                if (origAgentId) {
                    ctrl.model.selectedAgent = ctrl.onlineAgents.find(function (a) {
                        return (a.agentId || a.id) === origAgentId;
                    }) || null;
                }
            })
            .catch(function () { ctrl.onlineAgents = []; })
            .finally(function () { ctrl.loadingAgents = false; });
    }

    // ================================================================
    // URL MODE — path change + fetch
    // ================================================================

    ctrl.onAgentChange = function () {
        ctrl.fetchedOk       = false;
        ctrl.fetchError      = null;
        ctrl.newSpecObj      = null;
        ctrl.versionMismatch = false;
        ctrl.titleMismatch   = false;
    };

    ctrl.onPathChange = function () {
        ctrl.fetchedOk       = false;
        ctrl.fetchError      = null;
        ctrl.newSpecObj      = null;
        ctrl.versionMismatch = false;
        ctrl.titleMismatch   = false;

        if (ctrl._urlDebounce) $timeout.cancel(ctrl._urlDebounce);
        ctrl._urlDebounce = $timeout(function () {
            ctrl.urlError = _validatePath(ctrl.model.swaggerPath);
        }, 400);
    };

    ctrl.canFetch = function () {
        return ctrl.model.selectedAgent !== null &&
               ctrl.model.swaggerPath && ctrl.model.swaggerPath.trim().length > 0 &&
               !ctrl.urlError;
    };

    ctrl.fetchFromAgent = function () {
        if (!ctrl.canFetch()) return;

        const fullUrl = 'https://' + ctrl.model.swaggerPath.trim();
        const agentId = ctrl.model.selectedAgent.agentId || ctrl.model.selectedAgent.id;

        ctrl.isFetching  = true;
        ctrl.fetchedOk   = false;
        ctrl.fetchError  = null;
        ctrl.newSpecObj  = null;
        ctrl.versionMismatch = false;

        TelemetryAgentService.fetchSwaggerContract(agentId, fullUrl)
            .then(function (response) {
                const data    = response.data;
                const specObj = _parseSpec(data.rawContent, data.contentType);
                const valErr  = _validateSpec(specObj);

                if (valErr) return $q.reject({ _validationError: valErr });

                ctrl.newSpecObj     = specObj;
                ctrl.newSpecRaw     = data.rawContent;
                ctrl.newSpecVersion = specObj.info && specObj.info.version || null;
                ctrl.newSpecTitle   = specObj.info && specObj.info.title   || null;
                ctrl.versionMismatch = ctrl.newSpecVersion !== collection.api_version;
                ctrl.titleMismatch   = ctrl.newSpecTitle   !== collection.collection_name;
                ctrl.fetchedOk  = true;
                ctrl.fetchedLabel = _describeSpec(specObj);
            })
            .catch(function (err) {
                const msg = err._validationError ||
                    (err.data && err.data.message) ||
                    err.message ||
                    'Could not fetch the spec. Check the URL and try again.';
                ctrl.fetchError = msg;
            })
            .finally(function () { ctrl.isFetching = false; });
    };

    // ================================================================
    // FILE MODE
    // ================================================================

    ctrl.openFilePicker = function () {
        const input = document.getElementById('ace-file-input');
        if (input) input.click();
    };

    ctrl.onFileSelected = function (inputEl) {
        const file = inputEl.files && inputEl.files[0];
        if (!file) return;

        ctrl.newFile          = file;
        ctrl.newFileSizeLabel = _formatBytes(file.size);
        ctrl.newFileParsedOk  = false;
        ctrl.newFileError     = null;
        ctrl.newSpecObj       = null;
        ctrl.versionMismatch  = false;

        const reader = new FileReader();
        reader.onload = function (e) {
            $scope.$apply(function () {
                try {
                    const raw  = e.target.result;
                    const spec = _parseSpec(raw, file.name);
                    const err  = _validateSpec(spec);

                    if (err) {
                        ctrl.newFileError = err;
                        return;
                    }

                    ctrl.newSpecObj     = spec;
                    ctrl.newSpecRaw     = raw;
                    ctrl.newSpecVersion = spec.info && spec.info.version || null;
                    ctrl.newSpecTitle   = spec.info && spec.info.title   || null;
                    ctrl.versionMismatch = ctrl.newSpecVersion !== collection.api_version;
                    ctrl.titleMismatch   = ctrl.newSpecTitle   !== collection.collection_name;
                    ctrl.newFileParsedOk = true;
                    ctrl.newFileLabel    = _describeSpec(spec);
                } catch (e) {
                    ctrl.newFileError = 'Could not parse the file — make sure it is a valid JSON or YAML OpenAPI spec.';
                }
            });
        };

        reader.onerror = function () {
            $scope.$apply(function () {
                ctrl.newFileError = 'Failed to read the file. Please try again.';
            });
        };

        reader.readAsText(file);
    };

    ctrl.clearNewFile = function () {
        ctrl.newFile          = null;
        ctrl.newFileSizeLabel = '';
        ctrl.newFileParsedOk  = false;
        ctrl.newFileError     = null;
        ctrl.newFileLabel     = '';
        ctrl.newSpecObj      = null;
        ctrl.newSpecRaw      = null;
        ctrl.newSpecVersion  = null;
        ctrl.newSpecTitle    = null;
        ctrl.versionMismatch = false;
        ctrl.titleMismatch   = false;
        const input = document.getElementById('ace-file-input');
        if (input) input.value = '';
    };

    // ================================================================
    // DIRTY DETECTION
    // ================================================================

    ctrl.isDirty = function () {
        // A contract confirmed in the Swagger editor is already server-validated.
        if (ctrl.editorStaged) return true;

        var sourceTouched = (ctrl.sourceType === 'FILE'     && ctrl.newFile !== null) ||
                            (ctrl.sourceType === 'HTTP_URL' && _urlOrAgentChanged());

        if (sourceTouched) {
            // Source was touched: description change is irrelevant.
            // Button only enables when fetch/parse succeeded AND no version mismatch.
            return (ctrl.sourceType === 'FILE'     && ctrl.newFileParsedOk && !ctrl.versionBlocks() && !ctrl.titleMismatch) ||
                   (ctrl.sourceType === 'HTTP_URL' && ctrl.fetchedOk       && !ctrl.versionBlocks() && !ctrl.titleMismatch);
        }

        // Source not touched: only description change matters.
        return ctrl.model.description !== _orig.description;
    };

    // A new spec from file / URL with another version: an unpublished collection is
    // edited in place, so the version must match; a PUBLISHED one is forked into
    // that version, which must be higher (the backend also rejects one already taken).
    ctrl.versionBlocks = function () {
        if (!ctrl.versionMismatch) return false;
        return !ctrl.isPublished || !ctrl.newVersionIsHigher();
    };

    ctrl.newVersionIsHigher = function () {
        return _compareVersions(ctrl.newSpecVersion, collection.api_version) > 0;
    };

    function _compareVersions(a, b) {
        function nums(v) {
            var core = String(v || '').trim().replace(/^v/i, '').split(/[-+]/)[0].split('.');
            return [0, 1, 2].map(function (i) { return parseInt(core[i], 10) || 0; });
        }
        var x = nums(a), y = nums(b);
        for (var i = 0; i < 3; i++) { if (x[i] !== y[i]) return x[i] - y[i]; }
        return 0;
    }

    function _urlOrAgentChanged() {
        var currentPath    = (ctrl.model.swaggerPath || '').trim();
        var currentAgentId = ctrl.model.selectedAgent
            ? (ctrl.model.selectedAgent.agentId || ctrl.model.selectedAgent.id)
            : null;
        return currentPath !== _orig.swaggerPath || currentAgentId !== _orig.agentId;
    }

    // ================================================================
    // APPLY CHANGES
    // ================================================================

    ctrl.applyChanges = function () {
        if (!ctrl.isDirty()) return;

        ctrl.isSaving = true;

        const payload = {};

        // Description
        if (ctrl.model.description !== _orig.description) {
            payload.description = ctrl.model.description;
        }

        // Contract edited in the Swagger editor: the source (file / URL) is kept as is.
        if (ctrl.editorStaged) {
            payload.swagger = ctrl.editorStaged.spec;
        }
        // New swagger (file or fetched)
        else if (ctrl.newSpecObj) {
            payload.swagger = ctrl.newSpecObj;

            if (ctrl.sourceType === 'FILE') {
                payload.swagger_source = {
                    source_type: 'FILE',
                    source_file_name: ctrl.newFile ? ctrl.newFile.name : null
                };
            } else if (ctrl.sourceType === 'HTTP_URL') {
                payload.swagger_source = {
                    source_type: 'HTTP_URL',
                    source_url: 'https://' + ctrl.model.swaggerPath.trim(),
                    origin_agent: {
                        id:   ctrl.model.selectedAgent.agentId || ctrl.model.selectedAgent.id,
                        name: ctrl.model.selectedAgent.agentName || ctrl.model.selectedAgent.name
                    }
                };
            }
        }

        var save = ctrl.isPublished
            ? ApiCollectionService.fork(collection.collection_id, payload)
            : ApiCollectionService.patch(collection.collection_id, payload);

        save
            .then(function (result) {
                $mdDialog.hide(ctrl.isPublished
                    ? { forked: true, collection: result }
                    : { updated: true, collection: result });
            })
            .catch(function (err) {
                dialogService.showErrorDialog(
                    'Save Failed',
                    dialogService.formatErrorMessage({
                        error:     (err.data && err.data.error)     || 'Save Error',
                        message:   (err.data && err.data.message)   || 'Could not save the changes. Please try again.',
                        path:      (err.data && err.data.path)      || '/v1/api/bizmetry/api-collection/' + collection.collection_id,
                        timestamp: (err.data && err.data.timestamp) || new Date().toISOString()
                    })
                );
            })
            .finally(function () { ctrl.isSaving = false; });
    };

    // ================================================================
    // SWAGGER EDITOR
    // ================================================================

    var LOCKED_STATUSES = ['RETIRED'];

    ctrl.editSwaggerBlockedReason = function () {
        if (LOCKED_STATUSES.indexOf(ctrl.collection.status) !== -1) {
            return 'A ' + ctrl.collection.status + ' collection can\'t be edited.';
        }
        if (ctrl.newSpecObj || ctrl.newFile) {
            return 'A new contract from file / URL is staged — apply or discard it first.';
        }
        if (!ctrl.collection.swagger) { return 'This collection has no swagger to edit.'; }
        return null;
    };

    ctrl.canEditSwagger = function () {
        return !ctrl.isSaving && !ctrl.isFetching && ctrl.editSwaggerBlockedReason() === null;
    };

    /** Replacing from file / URL is blocked while an editor contract is staged. */
    ctrl.canReplaceSource = function () {
        return !ctrl.editorStaged;
    };

    ctrl.openSwaggerEditor = function () {
        if (!ctrl.canEditSwagger()) return;
        $mdDialog.show({
            controller:          'SwaggerEditorDialogController',
            controllerAs:        'ctrl',
            templateUrl:         'home/main-menu/api-collection/swagger-editor/swagger-editor-dialog.html',
            parent:              angular.element(document.body),
            clickOutsideToClose: false,
            escapeToClose:       false,       // Cancel handles the "discard changes?" confirmation
            multiple:            true,
            locals: {
                collection:  ctrl.collection,
                editorMode:  'edit',
                // Re-open on the staged contract (changes still measured against the saved one).
                initialSpec: ctrl.editorStaged ? ctrl.editorStaged.spec : null
            }
        }).then(function (result) {
            if (result && result.saved && result.spec) {
                ctrl.editorStaged = {
                    spec:            result.spec,
                    operationsCount: result.operationsCount,
                    pathsCount:      result.spec.paths ? Object.keys(result.spec.paths).length : 0,
                    changes:         result.changes || [],
                    changesTotal:    result.changesTotal || 0,
                    nextVersion:     result.nextVersion || null,
                    affectedBizEvents: result.affectedBizEvents
                };
            }
        }, angular.noop);
    };

    ctrl.discardEditorChanges = function () {
        ctrl.editorStaged = null;
    };

    function _refreshSummary() {
        var c = ctrl.collection || {};
        ctrl.operationsCount = Array.isArray(c.operations) ? c.operations.length : (c.operations_count || 0);
        ctrl.pathsCount = c.swagger && c.swagger.paths ? Object.keys(c.swagger.paths).length : 0;
    }

    // ================================================================
    // CANCEL
    // ================================================================

    ctrl.cancel = function () {
        $mdDialog.cancel();
    };

    // ================================================================
    // PRIVATE HELPERS
    // ================================================================

    function _pathFromUrl(swaggerSource) {
        if (!swaggerSource || swaggerSource.source_type !== 'HTTP_URL') return '';
        var url = swaggerSource.source_url || '';
        return url.replace(/^https?:\/\//i, '');
    }

    function _agentIdFromSource(swaggerSource) {
        if (!swaggerSource || !swaggerSource.origin_agent) return null;
        return swaggerSource.origin_agent.id || null;
    }

    function _validatePath(path) {
        if (!path || path.trim().length === 0) return null;
        const trimmed = path.trim();
        if (/^https?:\/\//i.test(trimmed)) {
            return 'Do not include the protocol — the "https://" prefix is already added.';
        }
        if (!URL_PATTERN.test(trimmed)) {
            return 'Invalid address. Enter a valid hostname or IP, e.g. my-service.internal/v3/api-docs';
        }
        return null;
    }

    function _validateSpec(spec) {
        if (!spec || typeof spec !== 'object') {
            return 'The content could not be parsed as a valid JSON or YAML document.';
        }
        const isOpenApi3 = typeof spec.openapi === 'string' && spec.openapi.startsWith('3.');
        const isSwagger2 = typeof spec.swagger === 'string' && spec.swagger.startsWith('2.');
        if (!isOpenApi3 && !isSwagger2) {
            return 'Not a valid OpenAPI (3.x) or Swagger (2.x) contract.';
        }
        if (!spec.info || !spec.info.title) {
            return 'The contract is missing the required "info.title" field.';
        }
        if (!spec.paths || Object.keys(spec.paths).length === 0) {
            return 'The contract has no paths defined.';
        }
        return null;
    }

    function _parseSpec(raw, hint) {
        if (!raw) return null;
        const h = (hint || '').toLowerCase();
        try {
            if (h.includes('yaml') || h.includes('yml')) {
                return window.jsyaml ? window.jsyaml.load(raw) : null;
            }
            try { return JSON.parse(raw); }
            catch (_) { return window.jsyaml ? window.jsyaml.load(raw) : null; }
        } catch (e) { return null; }
    }

    function _describeSpec(spec) {
        if (!spec) return 'Spec loaded';
        const paths = spec.paths ? Object.keys(spec.paths).length : 0;
        const title = spec.info && spec.info.title ? spec.info.title : 'OpenAPI';
        return title + ' — ' + paths + ' path' + (paths !== 1 ? 's' : '');
    }

    function _formatBytes(bytes) {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(2) + ' MB';
    }
});